/**
 * A segmented control that behaves like the radio group it says it is.
 *
 * Three controls on this surface carry `role="radiogroup"` with `role="radio"` children — the head's
 * column chips, the stepper's kg / % segment and the preview's scenario picker. Every one of them
 * was a radio group in NAME only: each option was an ordinary tab stop, so a trainer crossing the
 * row pressed Tab three times where APG gives one, and ArrowRight on „Ciężar" did nothing at all. A
 * screen-reader user hearing "radio, 1 of 3" and pressing the key that word promises got silence.
 *
 * The pattern is APG's, and it is not optional once the role is claimed: the CHECKED option holds
 * the group's single tab stop, the others are `tabIndex={-1}`, and Arrow / Home / End both select
 * and move focus. Selection follows focus here rather than being a second keystroke, because every
 * one of these three is a choice with no confirm step — the surface previews the consequence and
 * Zastosuj commits it.
 *
 * `groupRef` is handed IN rather than returned. The React Compiler refuses a ref written during
 * render, which is what `ref={hook.ref}` compiles to; every hook in this app that needs an element
 * takes its ref for the same reason.
 *
 * ── A group that handled an arrow key keeps it ───────────────────────────────────────────────
 * The propagation stop is not defensive bookkeeping: it is what makes the pattern safe to nest. On
 * the surface this was written for, a variant picker was rendered inside one option of the family
 * group, and without it an ArrowRight walked the inner group AND then the outer one — moving the
 * trainer to another card and unmounting the pill they were standing on. Nothing on the sheet nests
 * today; the rule stands because the alternative is a trap that reappears with the first control
 * that does. A key this group has already acted on does not belong to an ancestor group; a key it
 * ignores still does.
 */

import { type KeyboardEvent, type RefObject } from 'react';

export interface RovingRadioOptionProps<T extends string> {
  readonly role: 'radio';
  readonly 'aria-checked': boolean;
  readonly tabIndex: 0 | -1;
  readonly 'data-option': T;
  readonly onClick: () => void;
}

export interface RovingRadioGroup<T extends string> {
  readonly groupProps: {
    readonly role: 'radiogroup';
    readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
  readonly optionProps: (option: T) => RovingRadioOptionProps<T>;
}

interface RovingRadioGroupInput<T extends string> {
  readonly groupRef: RefObject<HTMLElement | null>;
  readonly options: readonly T[];
  /** The option currently chosen, or `null` when the group is showing none. */
  readonly value: T | null;
  readonly onSelect: (option: T) => void;
}

export function useRovingRadioGroup<T extends string>({
  groupRef,
  options,
  value,
  onSelect,
}: RovingRadioGroupInput<T>): RovingRadioGroup<T> {
  const checkedIndex = value === null ? -1 : options.indexOf(value);
  // A group showing nothing checked still needs exactly one tab stop, or it drops out of the tab
  // order entirely. APG's answer is the first option, and this group can reach that state: the Oś
  // control lists the metrics the MOVEMENT measures, and a stored rule can name one it does not.
  const stopIndex = checkedIndex === -1 ? 0 : checkedIndex;

  const move = (index: number) => {
    const wrapped = ((index % options.length) + options.length) % options.length;
    const option = options[wrapped];
    if (option === undefined) return;
    onSelect(option);
    /* SYNCHRONOUSLY, for the reason `matrixGridInteraction`'s `focusCell` states and measures.
     *
     * This waited a frame on the belief that an option still carrying `tabIndex={-1}` would refuse
     * the move until React re-rendered. It does not: `tabIndex={-1}` takes an element OUT of the tab
     * order and has never refused a programmatic `focus()`, so there was nothing to wait for. What
     * the wait did instead was hand the browser a frame with no focus in this group, which the
     * sheet's own trap is free to claim. */
    groupRef.current?.querySelector<HTMLElement>(`[data-option="${option}"]`)?.focus();
  };

  return {
    groupProps: {
      role: 'radiogroup',
      onKeyDown: (event) => {
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            move(stopIndex + 1);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            move(stopIndex - 1);
            break;
          case 'Home':
            move(0);
            break;
          case 'End':
            move(options.length - 1);
            break;
          default:
            return;
        }
        event.preventDefault();
        event.stopPropagation();
      },
    },
    optionProps: (option) => ({
      role: 'radio',
      'aria-checked': option === value,
      tabIndex: options.indexOf(option) === stopIndex ? 0 : -1,
      'data-option': option,
      onClick: () => {
        onSelect(option);
      },
    }),
  };
}
